import { isPlatformBrowser } from '@angular/common';
import { EnvironmentProviders, PLATFORM_ID, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core';
import { register } from 'swiper/element';

import { SWIPER_DEFAULT_OPTIONS, SWIPER_THEMES } from '../../tokens/swiper.token';
import { SWIPER_BUILT_IN_THEMES } from './swiper.themes';
import { SwiperElementOptions, SwiperThemes } from './swiper.types';

/** The configuration accepted by `provideSwiper`. */
export interface SwiperConfig {
    /** Global default options applied to every swiper (lowest precedence after the built-ins). */
    options?: SwiperElementOptions;

    /** Additional theme presets, merged over the built-in themes (same-name themes are replaced). */
    themes?: SwiperThemes;
}

/**
 * Registers the Swiper custom elements (`<swiper-container>` / `<swiper-slide>`, core build,
 * browser only) and provides the global default options and the theme registry.
 *
 * Add this once to the application's root providers:
 * `providers: [provideSwiper({ options: { speed: 400 } })]`.
 *
 * @param config - Optional global default options and additional theme presets.
 * @returns {EnvironmentProviders} The environment providers for the swiper feature.
 */
export function provideSwiper(config?: SwiperConfig): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideEnvironmentInitializer(() => {
            if (isPlatformBrowser(inject(PLATFORM_ID))) {
                register();
            }
        }),
        {
            provide: SWIPER_DEFAULT_OPTIONS,
            useValue: config?.options ?? {},
        },
        {
            provide: SWIPER_THEMES,
            useValue: mergeThemes(config?.themes),
        },
    ]);
}

/**
 * Provides additional or overriding theme presets for a feature, route or component subtree
 * without touching element registration or the global default options.
 *
 * @param themes - The theme presets to merge over the built-in themes.
 * @returns {EnvironmentProviders} The environment providers carrying the merged theme registry.
 */
export function provideSwiperThemes(themes: SwiperThemes): EnvironmentProviders {
    return makeEnvironmentProviders([
        {
            provide: SWIPER_THEMES,
            useValue: mergeThemes(themes),
        },
    ]);
}

/**
 * Merges custom theme presets over the built-in themes.
 *
 * @param themes - The custom themes to merge, if any.
 * @returns {SwiperThemes} The combined theme registry.
 */
function mergeThemes(themes?: SwiperThemes): SwiperThemes {
    return {
        ...SWIPER_BUILT_IN_THEMES,
        ...(themes ?? {}),
    };
}
